import React from 'react';
import Slick from 'react-slick';
import styled from 'styled-components';
import { variant, color, background, layout } from 'styled-system';
import { themeGet } from '@styled-system/theme-get';
import 'slick-carousel/slick/slick.css';
import next from './next.svg';
import prev from './prev.svg';
import { useState } from 'react';
import { useCallback } from 'react';

const carouselIndexStyle = variant({
  key: 'carousel.index',
});

const carouselArrowStyle = variant({
  key: 'carousel.arrow',
});

const carouselDotsStyle = variant({
  key: 'carousel.dots',
});

const Wrap = styled.div`
  position: relative;
`;

const Index = styled.div`
  position: absolute;
  top: ${themeGet('carousel.index.top')};
  right: ${themeGet('carousel.index.right')};
  bottom: ${themeGet('carousel.index.bottom')};
  left: ${themeGet('carousel.index.left')};
  padding: ${themeGet('carousel.index.padding')};
  background-color: ${themeGet('carousel.index.backgroundColor')};
  color: ${themeGet('carousel.index.color')};
  z-index: 1;
  border-radius: ${themeGet('carousel.index.borderRadius')};
  font-size: ${themeGet('carousel.index.fontSize')};
  ${carouselIndexStyle}
`;

const SlideWrap = styled.a`
  display: block;
  ${layout}
  ${color}
  ${background}
  background-repeat: no-repeat;
  text-decoration: none;
  cursor: pointer;
`;

SlideWrap.defaultProps = {
  height: '300px',
  color: 'white',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundColor: 'primaryLight',
};

const SlideContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  word-break: break-word;
  height: inherit;
`;

const SlideTitle = styled.h2`
  width: 60%;
  line-height: 1.5;
  font-size: ${themeGet('carousel.slideTitle.fontSize')};
  font-weight: ${themeGet('carousel.slideTitle.fontWeight')};
  color: ${themeGet('carousel.slideTitle.color')};
  margin: 0;
`;

const SlideDescription = styled.p`
  width: 60%;
  line-height: 1.5;
  font-size: ${themeGet('carousel.slideDescription.fontSize')};
  color: ${themeGet('carousel.slideDescription.color')};
  margin-top: 8px;
`;

const Slide = ({ title, description, ...rest }) => (
  <SlideWrap rel="noopener" target="_blank" {...rest}>
    <SlideContent>
      <SlideTitle>{title}</SlideTitle>
      <SlideDescription>{description}</SlideDescription>
    </SlideContent>
  </SlideWrap>
);

const CustomPage = styled(SlideWrap)`
  display: inline-block;
  width: ${themeGet('carousel.customPage.width')};
  height: ${themeGet('carousel.customPage.height')};
  background-image: ${props => props.backgroundImage};
`;

const StyledSlick = styled(Slick)`
  position: relative;
  display: block;

  .slick-arrow {
    position: absolute;
    top: ${themeGet('carousel.arrow.top')};
    bottom: ${themeGet('carousel.arrow.bottom')};
    width: ${themeGet('carousel.arrow.width')};
    height: ${themeGet('carousel.arrow.height')};
    border: ${themeGet('carousel.arrow.border')};
    border-radius: ${themeGet('carousel.arrow.borderRadius')};
    background-color: ${themeGet('carousel.arrow.backgroundColor')};
    z-index: 1;
    color: transparent;
    overflow: hidden;
    background-position: center;
    background-repeat: no-repeat;
    outline: none;
    cursor: pointer;
    ${carouselArrowStyle}

    &.slick-prev {
      left: ${themeGet('carousel.arrow.prev.left')};
      right: ${themeGet('carousel.arrow.prev.right')};
      background-image: url(${prev});
    }

    &.slick-next {
      left: ${themeGet('carousel.arrow.next.left')};
      right: ${themeGet('carousel.arrow.next.right')};
      background-image: url(${next});
    }
  }

  .slick-dots {
    position: ${themeGet('carousel.dots.position')};
    left: ${themeGet('carousel.dots.left')};
    bottom: ${themeGet('carousel.dots.bottom')};
    display: block;
    width: 100%;
    margin: 0;
    padding: 0;
    text-align: ${themeGet('carousel.dots.textAlign')};
    list-style: none;
    ${carouselDotsStyle}

    &.slick-thumb {
      display: flex !important;
      overflow: auto;
      width: 100%;
      bottom: auto;

      li {
        margin: ${themeGet('carousel.customPage.margin')};

        &.slick-active {
          ${CustomPage} {
            border: ${themeGet('carousel.customPage.border')};
            border-color: ${themeGet('carousel.customPage.borderColor')};
            box-sizing: border-box;
          }
        }
      }
    }

    li {
      position: relative;
      display: inline-flex;
      text-align: center;
      vertical-align: top;

      &.slick-active {
        button {
          width: ${themeGet('carousel.dot.active.width')};
          height: ${themeGet('carousel.dot.active.height')};
          border-radius: ${themeGet('carousel.dot.active.borderRadius')};
          background-color: ${themeGet('carousel.dot.active.backgroundColor')};
        }
      }

      button {
        display: block;
        width: ${themeGet('carousel.dot.width')};
        height: ${themeGet('carousel.dot.height')};
        padding: 0;
        margin: ${themeGet('carousel.dot.margin')};
        color: transparent;
        font-size: 0;
        background-color: ${themeGet('carousel.dot.backgroundColor')};
        border: ${themeGet('carousel.dot.border')};
        border-radius: ${themeGet('carousel.dot.borderRadius')};
        outline: none;
        cursor: pointer;
        -webkit-transition: all 0.5s;
        transition: all 0.5s;
      }
    }
  }
`;

export const Carousel = ({
  autoplay,
  autoplaySpeed,
  afterChange,
  beforeChange,
  dots,
  arrows,
  draggable,
  children,
  variant,
  index,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const handleBeforeChange = useCallback(
    (_, currentIndex) => {
      setCurrentIndex(currentIndex);
      return beforeChange;
    },
    [beforeChange]
  );

  const pages = React.Children.map(children, page => (
    <CustomPage backgroundImage={page.props.backgroundImage} />
  ));

  const total = React.Children.count(children);

  const galleryProps = variant === 'gallery' && {
    customPaging: i => pages[i],
    dotsClass: 'slick-dots slick-thumb',
    responsive: [
      {
        breakpoint: 1032,
        settings: {
          dots: false,
        },
      },
    ],
  };

  const carouselVariant = variant === 'gallery' ? ['default', 'gallery'] : 'default';

  return (
    <Wrap>
      {index && total > 1 && (
        <Index variant={carouselVariant}>
          {currentIndex + 1} / {total}
        </Index>
      )}
      <StyledSlick
        {...galleryProps}
        dots={dots}
        autoplaySpeed={autoplaySpeed}
        autoplay={autoplay}
        arrows={arrows}
        draggable={draggable}
        beforeChange={handleBeforeChange}
        afterChange={afterChange}
        variant={carouselVariant}
      >
        {children}
      </StyledSlick>
    </Wrap>
  );
};

Carousel.defaultProps = {
  draggable: false,
  arrows: false,
  dots: true,
  autoplay: false,
  autoplaySpeed: 3000,
};

Carousel.Slide = Slide;
